/** @format */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, from, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ProfileService, UserService } from 'src/app/services';
import { getAllUsers, getTeamProfile } from 'src/app/store';
import {
	ADGroupList,
	AdminUserView,
	ApplicationState,
	CreateNewUser,
	GetAllUsers,
	GetTeamProfilePicsBulk,
	TeamMemberProfile,
	UpdateExistingUser,
	UpdateUserAccount,
	User,
	UserAdGroupEntry,
} from 'src/app/store/application';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';

@Component({
	selector: 'app-accounts-panel',
	templateUrl: './accounts-panel.component.html',
	styleUrls: ['./accounts-panel.component.scss'],
})
export class AccountsPanel implements OnInit, OnDestroy {
	readonly UsersPerPage: number = 10;

	private ngUnsubscribe: Subject<void> = new Subject();

	@ViewChild('accountsModal') accountsModal: any;
	accountsModalRef!: NgbModalRef;
	isAccountUpdateModal: boolean = false;

	allUsers$!: Observable<User[]>;
	allUsers: User[] = [];
	filteredUsers: User[] = [];
	searchResultsUsersList: User[] = [];
	selectedModalUser?: User;

	managerUsers: User[] = [];
	timekeeperUsers: User[] = [];

	userProfiles$!: Observable<TeamMemberProfile[]>;
	userProfiles: TeamMemberProfile[] = [];

	userAdGroupMappings: UserAdGroupEntry[] = [];
	userAdGroupMappingsNeedRefresh: boolean = false;

	searchText: string = '';
	totalContentLength = 0;

	paginationStartingPosition: number = 1;

	accountsForm: FormGroup = new FormGroup({
		firstName: new FormControl('', Validators.required),
		lastName: new FormControl('', Validators.required),
		upn: new FormControl('', Validators.required),
		email: new FormControl('', Validators.required),
		active: new FormControl(true, Validators.required),
		employeeNum: new FormControl('', Validators.required),
		isManager: new FormControl(false, Validators.required),
		isTimekeeper: new FormControl(false, Validators.required),
		isHR: new FormControl(false, Validators.required),
		manager: new FormControl(null, Validators.required),
		timekeeper: new FormControl(null, Validators.required),
		isClosePostingsIgnored: new FormControl(false, Validators.required),
	});

	constructor(
		private store: Store<ApplicationState>,
		private authService: MsalService,
		private modalService: NgbModal,
		private profileService: ProfileService,
		private userService: UserService
	) {
		this.allUsers$ = this.store.select(getAllUsers);
		this.userProfiles$ = this.store.select(getTeamProfile);
	}

	ngOnInit(): void {
		this.allUsers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((allUsers) => {
			let needsUsersReload = allUsers.length !== this.allUsers.length && allUsers.length > 0;
			this.userAdGroupMappingsNeedRefresh = needsUsersReload;

			if (this.userAdGroupMappingsNeedRefresh) {
				this.userAdGroupMappings = [];
			}

			this.allUsers = [...allUsers]
				.sort((a: User, b: User) => {
					let nameA = this.getUserFullName(a).toLowerCase();
					let nameB = this.getUserFullName(b).toLowerCase();

					if (nameA < nameB) return -1;
					if (nameA > nameB) return 1;
					return 0;
				})
				.map((user) => ({
					...user,
					enrolledGroups: 'N/A',
				}));

			if (this.allUsers.length > 0) {
				this.assignADGroupsToUsersList();
			}

			this.generateManagerAndTimekeeperLists();
			this.totalContentLength = allUsers.length;
			this.updatePaginationDetails(this.paginationStartingPosition);

			if (needsUsersReload) {
				let upns = allUsers.map((user) => user.upn);
				this.store.dispatch(GetTeamProfilePicsBulk({ upns }));
				this.updatePaginationDetails(1);
			}
		});

		this.userProfiles$?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((team) => (this.userProfiles = team));
		this.store.dispatch(GetAllUsers());
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.unsubscribe();
	}

	generateManagerAndTimekeeperLists() {
		this.managerUsers = this.allUsers.filter((u) => u.isManager);
		this.timekeeperUsers = this.allUsers.filter((u) => u.isTimekeeper);
	}

	includeGeneratedBlankManagerAndTimekeeper() {
		let { selfManager, selfTimekeeper } = this.generateBlankManagerAndTimekeeper();
		this.managerUsers.unshift(selfManager);
		this.timekeeperUsers.unshift(selfTimekeeper);
	}

	removeGeneratedBlankManagerAndTimekeeper() {
		this.managerUsers.shift();
		this.timekeeperUsers.shift();
	}

	generateBlankManagerAndTimekeeper() {
		let blankSelfManagerUser = HelperFunctions.getBlankUser();
		let blankSelfTimekeeperUser = HelperFunctions.getBlankUser();

		blankSelfManagerUser.id = 0;
		blankSelfManagerUser.firstName = 'Self-Managed';
		blankSelfManagerUser.lastName = 'Manager';
		blankSelfManagerUser.active = false;

		blankSelfTimekeeperUser.id = 0;
		blankSelfTimekeeperUser.firstName = 'Self-Managed';
		blankSelfTimekeeperUser.lastName = 'Timekeeper';
		blankSelfTimekeeperUser.active = false;

		return { selfManager: blankSelfManagerUser, selfTimekeeper: blankSelfTimekeeperUser };
	}

	updatePaginationDetails(newStartingPosition: number, filterableUsers: User[] = this.allUsers) {
		this.paginationStartingPosition = newStartingPosition;
		let paginationPositionLimit = newStartingPosition + this.UsersPerPage;
		this.filteredUsers = this.searchText.length
			? [...this.searchResultsUsersList].filter((_, index) => index + 1 >= newStartingPosition && index + 1 < paginationPositionLimit)
			: filterableUsers.filter((_, index) => index + 1 >= newStartingPosition && index + 1 < paginationPositionLimit);
	}

	handleSearchInput() {
		if (!this.searchText.length) {
			this.updatePaginationDetails(1);
			this.totalContentLength = this.allUsers.length;
			return;
		}

		let searchTextManicured = this.searchText.toLowerCase().trim();
		let filteredResults = this.allUsers.filter(
			(user) =>
				this.getUserFullName(user).toLowerCase().includes(searchTextManicured) ||
				user.enrolledGroups?.toLowerCase().includes(searchTextManicured)
			// || user.email.toLowerCase().includes(searchTextManicured)
			// || user.employeeNumber.toString().includes(searchTextManicured)
			// || this.getUserRole(user).toLowerCase().includes(searchTextManicured)
			// || this.getTimekeeper(user).toLowerCase().includes(searchTextManicured)
			// || this.getUserActiveState(user).toLowerCase().includes(searchTextManicured)
		);

		this.searchResultsUsersList = filteredResults;
		this.updatePaginationDetails(1, filteredResults);
		this.totalContentLength = filteredResults.length;
	}

	getProfilePicture(user: User) {
		const member = this.userProfiles.find((queryUser) => HelperFunctions.compareEmail(queryUser.email, user.email || user.upn));
		if (member) return member.profilePic;
		return null;
	}

	getUserFullName = (user: User) => `${user.firstName} ${user.lastName}`.trim();

	getUserRole(user: User) {
		let roleString = 'Staff';
		if (user.isManager) roleString += ' - Manager';
		if (user.isTimekeeper) roleString += ' - Timekeeper';
		if (user.isHR) roleString += ' - HR';
		return roleString;
	}

	getManager(user: User) {
		if (!user.managerId) return 'N/A';
		let managerUser = this.allUsers.find((mu) => mu.id === user.managerId)!;
		return this.getUserFullName(managerUser);
	}

	getTimekeeper(user: User) {
		if (!user.timekeeperId) return 'N/A';
		let timekeeperUser = this.allUsers.find((tu) => tu.id === user.timekeeperId)!;
		return this.getUserFullName(timekeeperUser);
	}

	getCreatedAtDate(user: User) {
		if (!user.createdAt) return 'N/A';
		let createdDate = new Date(user.createdAt);
		return HelperFunctions.getDateString(createdDate);
	}

	getUserActiveState = (user: User) => (user.active ? 'Active' : 'Inactive');
	getControl = (controlName: string) => this.accountsForm.get(controlName)!;
	formatter = (x: any) => (x.firstName ? x.firstName : x);

	setSelfManagerLock() {
		if (!this.isAccountUpdateModal) {
			this.managerUsers[0].active = !this.managerUsers[0].active;
		}
	}

	setSelfTimekeeperLock() {
		if (!this.isAccountUpdateModal) {
			this.timekeeperUsers[0].active = !this.timekeeperUsers[0].active;
		}
	}

	handleAddNewUser() {
		this.isAccountUpdateModal = false;
		delete this.selectedModalUser;
		this.includeGeneratedBlankManagerAndTimekeeper();
		this.openUserManagementModal();
	}

	handleUserEntryClick(user: User) {
		this.setEditModalDetails(user);
		this.isAccountUpdateModal = true;
		this.selectedModalUser = user;
		this.openUserManagementModal();
	}

	setEditModalDetails(user: User) {
		this.accountsForm.controls.firstName.setValue(user.firstName);
		this.accountsForm.controls.lastName.setValue(user.lastName);
		this.accountsForm.controls.upn.setValue(user.upn);
		this.accountsForm.controls.email.setValue(user.email);
		this.accountsForm.controls.active.setValue(user.active);
		this.accountsForm.controls.employeeNum.setValue(user.employeeNumber || '');
		this.accountsForm.controls.isManager.setValue(user.isManager);
		this.accountsForm.controls.isTimekeeper.setValue(user.isTimekeeper);
		this.accountsForm.controls.isHR.setValue(user.isHR);
		this.accountsForm.controls.manager.setValue(this.managerUsers.find((u) => u.id === user.managerId)?.id);
		this.accountsForm.controls.timekeeper.setValue(this.timekeeperUsers.find((u) => u.id === user.timekeeperId)?.id);
		this.accountsForm.controls.isClosePostingsIgnored.setValue(user.isClosePostingsIgnored || false);
	}

	openUserManagementModal() {
		const ngbModalOptions: NgbModalOptions = {
			backdrop: 'static',
			keyboard: false,
			centered: true,
			size: 'lg',
			windowClass: 'accountsManagementModal',
		};

		this.accountsModalRef = this.modalService.open(this.accountsModal, ngbModalOptions);
		this.accountsModalRef.result.then((shouldSaveChanges: boolean) => {
			if (shouldSaveChanges) {
				let active =
					typeof this.accountsForm.value.active === 'string' ? this.accountsForm.value.active === 'true' : this.accountsForm.value.active;

				let isManager =
					typeof this.accountsForm.value.isManager === 'string'
						? this.accountsForm.value.isManager === 'true'
						: this.accountsForm.value.isManager;

				let isTimekeeper =
					typeof this.accountsForm.value.isTimekeeper === 'string'
						? this.accountsForm.value.isTimekeeper === 'true'
						: this.accountsForm.value.isTimekeeper;

				let isHR = typeof this.accountsForm.value.isHR === 'string' ? this.accountsForm.value.isHR === 'true' : this.accountsForm.value.isHR;

				const user = {
					id: this.selectedModalUser ? this.selectedModalUser.id : null,
					firstName: this.formatter(this.accountsForm.value.firstName),
					lastName: this.accountsForm.value.lastName,
					upn: this.accountsForm.value.upn,
					email: this.accountsForm.value.email,
					employeeNumber: this.accountsForm.value.employeeNum,
					active,
					isManager,
					isTimekeeper,
					isHR,
					managerId: this.accountsForm.value.manager,
					timekeeperId: this.accountsForm.value.timekeeper,
					isClosePostingsIgnored: this.accountsForm.value.isClosePostingsIgnored,
				} as User;

				if (this.isAccountUpdateModal) {
					let updatePayload: UpdateUserAccount = {
						oldAccountDetails: this.selectedModalUser!,
						newAccountDetails: user,
					};

					this.generateUserUpdatedHelpDeskEmail(updatePayload);
					this.store.dispatch(UpdateExistingUser({ updatedUserDetails: updatePayload }));
				} else {
					this.generateNewUserHelpDeskEmail(user);
					this.store.dispatch(CreateNewUser({ newUserDetails: user }));
				}
			}

			this.resetMemberForm();
		});
	}

	resetMemberForm() {
		this.accountsForm.reset();
		this.accountsForm.controls.firstName.setValue('');
		this.accountsForm.controls.lastName.setValue('');
		this.accountsForm.controls.upn.setValue('');
		this.accountsForm.controls.email.setValue('');
		this.accountsForm.controls.active.setValue(true);
		this.accountsForm.controls.employeeNum.setValue('');
		this.accountsForm.controls.isManager.setValue(false);
		this.accountsForm.controls.isTimekeeper.setValue(false);
		this.accountsForm.controls.isHR.setValue(false);
		this.accountsForm.controls.manager.setValue(null);
		this.accountsForm.controls.timekeeper.setValue(null);
		this.accountsForm.controls.isClosePostingsIgnored.setValue(false);

		if (!this.isAccountUpdateModal) {
			this.removeGeneratedBlankManagerAndTimekeeper();
		}
	}

	dismissModal(shouldSaveChanges: boolean = false) {
		this.accountsModalRef.close(shouldSaveChanges);
	}

	onAzureUserSelect(entry: any) {
		this.setUserFormValues(entry.item);
		this.azureManagerSearch(entry.item.upn);
	}

	setUserFormValues(user: AdminUserView) {
		const controls = this.accountsForm.controls;
		controls.firstName.setValue(user.firstName.trim());
		controls.lastName.setValue(user.lastName.trim());
		controls.email.setValue(user.email.trim());
		controls.upn.setValue(user.upn.trim());
		controls.active.setValue(user.active);
	}

	azureUserSearch = (text$: Observable<string>) => {
		return text$.pipe(
			debounceTime(50),
			distinctUntilChanged(),
			switchMap((name: string) => {
				const account = (
					this.authService.instance.getActiveAccount()
						? this.authService.instance.getActiveAccount()
						: this.authService.instance.getAllAccounts().filter((a) => !a.username.match(/adm@/i))[0]
				)!;

				const request = {
					scopes: ['User.Read'],
					account,
				};

				if (name.length === 0) {
					return of([]);
				}

				return forkJoin({
					token: from(this.authService.instance.acquireTokenSilent(request)),
					name: of(name),
				});
			}),
			switchMap((response: any) => {
				if (!response.token || !response.name) {
					return of([]);
				}

				return this.profileService.getADFilteredUsers(response.token.accessToken, response.name);
			}),
			map((response: any) => {
				const usersList: AdminUserView[] = [];

				(response.value || []).forEach((userEntry: any) => {
					if (
						userEntry.id &&
						userEntry.givenName &&
						userEntry.surname &&
						userEntry.mail &&
						userEntry.userPrincipalName &&
						!this.allUsers.some(
							(user) =>
								(user.email || '').toLowerCase() === userEntry.mail.toLowerCase() ||
								(user.upn || '').toLowerCase() === userEntry.userPrincipalName.toLowerCase()
						)
					) {
						usersList.push({
							id: userEntry.id,
							firstName: userEntry.givenName.trim(),
							lastName: userEntry.surname.trim(),
							email: userEntry.mail.trim(),
							active: true,
							upn: userEntry.userPrincipalName.trim(),
						});
					}
				});

				return [...usersList];
			}),
			catchError(() => of([]))
		);
	};

	azureManagerSearch = async (upn: string) => {
		// try to find the users employee number
		try {
			let employeeNumber = await this.userService.getADFilteredUserEmployeeId(upn);
			if (employeeNumber) {
				this.accountsForm.controls.employeeNum.setValue(employeeNumber);
			}
		} catch (error) {
			console.error(error);
		}

		// try to find the users manager and timekeeper
		try {
			let managerADProfile = await this.userService.getADFilteredUserManager(upn);
			if (managerADProfile) {
				let manager = this.managerUsers.find((u) => u.email.toLowerCase().trim() === managerADProfile.mail.toLowerCase().trim());

				if (manager) {
					this.accountsForm.controls.manager.setValue(manager.id);

					if (manager.timekeeperId) {
						let timekeeper = this.timekeeperUsers.find((tu) => tu.id === manager!.timekeeperId);
						if (timekeeper) {
							this.accountsForm.controls.timekeeper.setValue(timekeeper.id);
						}
					}
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	assignADGroupsToUsersList = async () => {
		try {
			if (this.userAdGroupMappingsNeedRefresh) {
				let groupMembers: ADGroupList = await this.userService.getTimesheetGroupMembers();
				console.log(groupMembers);

				this.allUsers.forEach((user) => {
					let enrolledGroups = '';
					if (groupMembers.appAuthTimesheetAdmin.includes(user.upn.toLowerCase())) enrolledGroups += 'AppAuthTimesheetAdmin ';
					if (groupMembers.appAuthTimesheetHR.includes(user.upn.toLowerCase())) enrolledGroups += 'AppAuthTimesheetHR ';
					if (groupMembers.appAuthTimesheetManager.includes(user.upn.toLowerCase())) enrolledGroups += 'AppAuthTimesheetManager ';
					if (groupMembers.appAuthTimesheetTimekeeper.includes(user.upn.toLowerCase())) enrolledGroups += 'AppAuthTimesheetTimekeeper ';
					if (groupMembers.appAuthTimesheetUsers.includes(user.upn.toLowerCase())) enrolledGroups += 'AppAuthTimesheetUser ';
					user.enrolledGroups = enrolledGroups.trim();

					this.userAdGroupMappings.push({
						upn: user.upn,
						adGroups: enrolledGroups.trim(),
					});
				});
			} else {
				this.allUsers.forEach((user) => {
					let matchingGroupMapping = this.userAdGroupMappings.find((mapping) => mapping.upn === user.upn);
					user.enrolledGroups = matchingGroupMapping?.adGroups;
				});
			}
		} catch (error) {
			console.error(error);
			return;
		}
	};

	userNeedHelpDeskUpdate = (updatedUserDetails: UpdateUserAccount) =>
		updatedUserDetails.oldAccountDetails.isHR !== updatedUserDetails.newAccountDetails.isHR ||
		updatedUserDetails.oldAccountDetails.isManager !== updatedUserDetails.newAccountDetails.isManager ||
		updatedUserDetails.oldAccountDetails.isTimekeeper !== updatedUserDetails.newAccountDetails.isTimekeeper;

	getUserGroupAssignmentsToChange(updatedUserDetails: UpdateUserAccount) {
		let oldDetails = updatedUserDetails.oldAccountDetails;
		let newDetails = updatedUserDetails.newAccountDetails;

		let assignmentsToAdd = '';
		let assignmentsToRemove = '';

		if (!oldDetails.isHR && newDetails.isHR) assignmentsToAdd += 'AppAuthTimesheetHR, ';
		if (oldDetails.isHR && !newDetails.isHR) assignmentsToRemove += 'AppAuthTimesheetHR, ';

		if (!oldDetails.isManager && newDetails.isManager) assignmentsToAdd += 'AppAuthTimesheetManager, ';
		if (oldDetails.isManager && !newDetails.isManager) assignmentsToRemove += 'AppAuthTimesheetManager, ';

		if (!oldDetails.isTimekeeper && newDetails.isTimekeeper) assignmentsToAdd += 'AppAuthTimesheetTimekeeper, ';
		if (oldDetails.isTimekeeper && !newDetails.isTimekeeper) assignmentsToRemove += 'AppAuthTimesheetTimekeeper, ';

		if (assignmentsToAdd.length > 0) {
			assignmentsToAdd = assignmentsToAdd.slice(0, assignmentsToAdd.length - 2);
		} else {
			assignmentsToAdd += 'N/A';
		}

		if (assignmentsToRemove.length > 0) {
			assignmentsToRemove = assignmentsToRemove.slice(0, assignmentsToRemove.length - 2);
		} else {
			assignmentsToRemove += 'N/A';
		}

		return { assignmentsToAdd, assignmentsToRemove };
	}

	generateUserUpdatedHelpDeskEmail(updatedUserDetails: UpdateUserAccount) {
		if (this.userNeedHelpDeskUpdate(updatedUserDetails)) {
			let { assignmentsToAdd, assignmentsToRemove } = this.getUserGroupAssignmentsToChange(updatedUserDetails);

			let oldDetails = updatedUserDetails.oldAccountDetails;
			let emailTo = 'helpdesk@dnv.org';
			let subject = `Timesheet user needs group membership changes - ${oldDetails.firstName} ${oldDetails.lastName}`;
			let body = `Hi,\n\nWe have a user needing changes to the group memberships they are a part of. The changes are down below.\n\nName: ${oldDetails.firstName} ${oldDetails.lastName}\nEmail: ${oldDetails.email}\nAssignments to add: ${assignmentsToAdd}\nAssignments to remove: ${assignmentsToRemove}\n\nRegards,\nTimesheet Admin`;
			location.href = `mailto:${emailTo}?subject=${subject}&body=${encodeURI(body)}`;
		}
	}

	generateNewUserHelpDeskEmail(newUser: User) {
		let emailTo = 'helpdesk@dnv.org';
		let subject = `New user being added to Timesheets - ${newUser.firstName} ${newUser.lastName}`;
		let body = `Hi,\n\nWe have a new user being added to the timesheet application. Could you please add them the appropriate groups if they are not already members? Their user information has been provided down below.\n\nName: ${
			newUser.firstName
		} ${newUser.lastName}\nEmail: ${newUser.email}\nAD Group Membership(s): AppAuthTimesheetUser${
			newUser.isManager ? ', AppAuthTimesheetManager' : ''
		}${newUser.isHR ? ', AppAuthTimesheetHR' : ''}${newUser.isTimekeeper ? ', AppAuthTimesheetTimekeeper' : ''}\n\nRegards,\nTimesheet Admin`;
		location.href = `mailto:${emailTo}?subject=${subject}&body=${encodeURI(body)}`;
	}
}
